export const DEFAULT_COLORS = {
  pageBackground: '#FFFFFF',
  cardBackground: '#FFFFFF',
  containerBorder: '#00C8FF',
  headingText: '#000000',
  bodyText: '#666666',
  link: '#00C8FF',
  chrome: '#00C8FF',
  button: '#00C8FF',
  buttonPrimaryText: '#000000',
  buttonPrimaryBorder: '#00C8FF',
  buttonPrimaryIcon: '#00C8FF',
  buttonSecondary: '#FFFFFF',
  buttonSecondaryText: '#00C8FF',
  buttonSecondaryBorder: '#00C8FF',
  buttonSecondaryIcon: '#00C8FF',
  loginButton: '#7A4035',
  loginButtonText: '#FFFFFF',
  loginTitle: '#FFFFFF',
  loginText: '#B3B3B3',
  loginSignup: '#FFFFFF',
  loginCardBackground: '#FFFFFF',
  subheaderText: '#666666',
  checkboxBorder: '#00C8FF',
  checkboxCheck: '#00C8FF',
  emailInputTitle: '#B3B3B3',
  emailInputBorder: '#B3B3B3',
  emailInputText: '#B3B3B3',
  passwordInputTitle: '#B3B3B3',
  passwordInputBorder: '#B3B3B3',
  passwordInputText: '#B3B3B3',
  loginButtonBorder: '#7A4035',
  inApp: '#00C8FF',
  inAppAccent: '#000000',
};

export const DEFAULT_OPACITIES = {
  pageBackground: 0,
  cardBackground: 100,
  containerBorder: 100,
  headingText: 100,
  bodyText: 100,
  link: 100,
  chrome: 100,
  button: 100,
  buttonPrimaryText: 100,
  buttonPrimaryBorder: 100,
  buttonPrimaryIcon: 100,
  buttonSecondary: 100,
  buttonSecondaryText: 100,
  buttonSecondaryBorder: 100,
  buttonSecondaryIcon: 100,
  loginButton: 100,
  loginButtonText: 100,
  loginTitle: 100,
  loginText: 100,
  loginSignup: 100,
  loginCardBackground: 100,
  subheaderText: 100,
  checkboxBorder: 100,
  checkboxCheck: 100,
  emailInputTitle: 100,
  emailInputBorder: 100,
  emailInputText: 100,
  passwordInputTitle: 100,
  passwordInputBorder: 100,
  passwordInputText: 100,
  loginButtonBorder: 100,
  inApp: 100,
  inAppAccent: 100,
};

export const DEFAULT_BLURS = {
  card: 50,
};

export const DEFAULT_LOGO_SIZE = 100;

const LEGACY_LOGO_SIZE_MAP = {
  small: 60,
  medium: 100,
  large: 140,
  xlarge: 180,
};

const BASE_LOGO_SIZES = {
  width: 200,
  maxHeight: 72,
  loginHeight: 40,
  headerHeight: 19,
};

export function normalizeHexInput(value) {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  const withHash = raw.startsWith('#') ? raw : `#${raw}`;
  if (/^#[0-9a-fA-F]{6}$/.test(withHash)) return withHash.toUpperCase();
  if (/^#[0-9a-fA-F]{3}$/.test(withHash)) {
    const r = withHash[1];
    const g = withHash[2];
    const b = withHash[3];
    return `#${r}${r}${g}${g}${b}${b}`.toUpperCase();
  }
  return '';
}

export function normalizeOpacityInput(value, fallback = 100) {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return fallback;
  }
  return Math.min(100, Math.max(0, Math.round(parsed)));
}

export function normalizeLogoSizeInput(value, fallback = 100) {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return fallback;
  }
  return Math.min(200, Math.max(10, Math.round(parsed)));
}

export function normalizeBlurInput(value, fallback = 0) {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return fallback;
  }
  return Math.min(100, Math.max(0, Math.round(parsed)));
}

export function toPickerValue(hex) {
  const normalized = normalizeHexInput(hex);
  return normalized || '#000000';
}

export function hexToCssColor(hex, opacityPercent) {
  const normalized = normalizeHexInput(hex);
  if (!normalized) return '';
  const opacity = normalizeOpacityInput(opacityPercent);
  if (opacity >= 100) return normalized;
  if (opacity <= 0) return 'transparent';
  const r = parseInt(normalized.slice(1, 3), 16);
  const g = parseInt(normalized.slice(3, 5), 16);
  const b = parseInt(normalized.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity / 100})`;
}

function readColor(data, key, fallback) {
  const legacyMap = {
    chrome: ['buttonBorder', 'buttonPrimaryBorder'],
    button: ['buttonFill'],
    buttonPrimaryBorder: ['button'],
    buttonPrimaryIcon: ['chrome'],
    buttonSecondaryBorder: ['button'],
    buttonSecondaryIcon: ['chrome'],
    containerBorder: ['chrome', 'buttonBorder'],
    emailInputTitle: ['loginInputTitle'],
    emailInputBorder: ['loginInputBorder'],
    emailInputText: ['loginInputText'],
    passwordInputTitle: ['loginInputTitle'],
    passwordInputBorder: ['loginInputBorder'],
    passwordInputText: ['loginInputText'],
    inApp: ['inAppColor'],
    inAppAccent: ['inAppAccentColor'],
    loginButton: ['loginButtonColor'],
    loginButtonText: ['loginButtonTextColor'],
    loginTitle: ['loginTitleColor'],
    loginText: ['loginTextColor'],
    loginSignup: ['loginSignupColor'],
  };
  const candidates = [key, ...(legacyMap[key] || [])];
  for (const candidate of candidates) {
    const normalized = normalizeHexInput(data?.[candidate]);
    if (normalized) return normalized;
  }
  if (key === 'buttonSecondary') {
    const pageBackground = normalizeHexInput(data?.pageBackground || data?.backgroundColor);
    if (pageBackground) return pageBackground;
  }
  return fallback;
}

function readOpacity(data, key, fallback) {
  const apiKey = `${key}Opacity`;
  if (data && Object.prototype.hasOwnProperty.call(data, apiKey)) {
    return normalizeOpacityInput(data[apiKey], fallback);
  }
  return fallback;
}

export function mapColorsFromApi(data) {
  if (!data) return { ...DEFAULT_COLORS };
  const mapped = Object.fromEntries(
    Object.keys(DEFAULT_COLORS).map((key) => [key, readColor(data, key, DEFAULT_COLORS[key])]),
  );
  const formBlockHex =
    normalizeHexInput(data?.cardBackground || data?.secondary) ||
    normalizeHexInput(data?.loginCardBackground) ||
    mapped.cardBackground;
  mapped.cardBackground = formBlockHex;
  mapped.loginCardBackground = formBlockHex;
  mapped.loginSignup = mapped.link;
  mapped.chrome = mapped.containerBorder;
  if (!normalizeHexInput(data?.subheaderText)) {
    mapped.subheaderText = mapped.bodyText;
  }
  if (!normalizeHexInput(data?.checkboxBorder)) {
    mapped.checkboxBorder = mapped.chrome;
  }
  if (!normalizeHexInput(data?.checkboxCheck)) {
    mapped.checkboxCheck = mapped.button;
  }
  if (!normalizeHexInput(data?.emailInputTitle) && !normalizeHexInput(data?.loginInputTitle)) {
    mapped.emailInputTitle = mapped.loginText;
  }
  if (!normalizeHexInput(data?.emailInputBorder) && !normalizeHexInput(data?.loginInputBorder)) {
    mapped.emailInputBorder = mapped.loginText;
  }
  if (!normalizeHexInput(data?.emailInputText) && !normalizeHexInput(data?.loginInputText)) {
    mapped.emailInputText = mapped.loginText;
  }
  if (!normalizeHexInput(data?.passwordInputTitle) && !normalizeHexInput(data?.loginInputTitle)) {
    mapped.passwordInputTitle = mapped.loginText;
  }
  if (!normalizeHexInput(data?.passwordInputBorder) && !normalizeHexInput(data?.loginInputBorder)) {
    mapped.passwordInputBorder = mapped.loginText;
  }
  if (!normalizeHexInput(data?.passwordInputText) && !normalizeHexInput(data?.loginInputText)) {
    mapped.passwordInputText = mapped.loginText;
  }
  if (!normalizeHexInput(data?.loginButton) && !normalizeHexInput(data?.loginButtonColor)) {
    mapped.loginButton = mapped.button;
  }
  if (!normalizeHexInput(data?.loginButtonBorder)) {
    mapped.loginButtonBorder = mapped.loginButton;
  }
  return mapped;
}

export function mapOpacitiesFromApi(data) {
  if (!data) return { ...DEFAULT_OPACITIES };
  const mapped = Object.fromEntries(
    Object.keys(DEFAULT_OPACITIES).map((key) => [
      key,
      readOpacity(data, key, DEFAULT_OPACITIES[key]),
    ]),
  );
  const formBlockOpacity = Object.prototype.hasOwnProperty.call(data || {}, 'cardBackgroundOpacity')
    ? mapped.cardBackground
    : mapped.loginCardBackground;
  mapped.cardBackground = formBlockOpacity;
  mapped.loginCardBackground = formBlockOpacity;
  mapped.loginSignup = mapped.link;
  mapped.chrome = mapped.containerBorder;
  return mapped;
}

export function mapBlursFromApi(data) {
  if (!data) return { ...DEFAULT_BLURS };
  const unifiedBlur = data?.cardBlur ?? data?.loginCardBlur ?? data?.emailSelectionCardBlur;
  return {
    card: normalizeBlurInput(unifiedBlur, DEFAULT_BLURS.card),
  };
}

export function mapLogoSizeFromApi(data) {
  const raw = data?.headerLogoSize;
  if (raw !== null && raw !== undefined && raw !== '') {
    const asNum = Number(raw);
    if (Number.isFinite(asNum) && asNum > 0) {
      return normalizeLogoSizeInput(asNum);
    }
    const str = String(raw).trim().toLowerCase();
    if (LEGACY_LOGO_SIZE_MAP[str] !== undefined) {
      return LEGACY_LOGO_SIZE_MAP[str];
    }
  }
  return DEFAULT_LOGO_SIZE;
}

export function getLogoSizeCssVars(logoSize) {
  const pct = normalizeLogoSizeInput(logoSize, DEFAULT_LOGO_SIZE) / 100;
  return {
    '--hc-brand-logo-width': `${Math.round(BASE_LOGO_SIZES.width * pct)}px`,
    '--hc-brand-logo-max-height': `${Math.round(BASE_LOGO_SIZES.maxHeight * pct)}px`,
    '--hc-login-logo-height': `${Math.round(BASE_LOGO_SIZES.loginHeight * pct)}px`,
    '--hc-header-logo-height': `${Math.round(BASE_LOGO_SIZES.headerHeight * pct)}px`,
  };
}

function cssColor(colors, opacities, key, fallbackHex) {
  const hex = normalizeHexInput(colors?.[key]) || fallbackHex;
  const opacity = normalizeOpacityInput(opacities?.[key], DEFAULT_OPACITIES[key] ?? 100);
  return hexToCssColor(hex, opacity);
}

function getRelativeLuminanceFromHex(hex) {
  const normalized = normalizeHexInput(hex);
  if (!normalized) return 0;
  const r = parseInt(normalized.slice(1, 3), 16);
  const g = parseInt(normalized.slice(3, 5), 16);
  const b = parseInt(normalized.slice(5, 7), 16);
  const [rs, gs, bs] = [r, g, b].map((value) => {
    const channel = value / 255;
    return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

function getPoweredByTextVars(backgroundHex, opacityPercent, fallbackHex = '#000000') {
  const opacity = normalizeOpacityInput(opacityPercent, 100);
  if (opacity <= 0) {
    return {
      '--hc-powered-by-text': '#FFFFFF',
      '--hc-powered-by-text-label': 'rgba(255, 255, 255, 0.62)',
      '--hc-powered-by-text-name': 'rgba(255, 255, 255, 0.92)',
    };
  }
  const hex = normalizeHexInput(backgroundHex) || fallbackHex;
  const useDarkText = getRelativeLuminanceFromHex(hex) > 0.179;
  if (useDarkText) {
    return {
      '--hc-powered-by-text': '#111111',
      '--hc-powered-by-text-label': 'rgba(17, 17, 17, 0.62)',
      '--hc-powered-by-text-name': 'rgba(17, 17, 17, 0.92)',
    };
  }
  return {
    '--hc-powered-by-text': '#FFFFFF',
    '--hc-powered-by-text-label': 'rgba(255, 255, 255, 0.62)',
    '--hc-powered-by-text-name': 'rgba(255, 255, 255, 0.92)',
  };
}

export function buildEmbedTheme({ colors, opacities, blurs, headerLogoSize } = {}) {
  const cardBlur = normalizeBlurInput(blurs?.card, DEFAULT_BLURS.card);
  const pageBackgroundHex = normalizeHexInput(colors?.pageBackground) || '#FFFFFF';
  const pageBackgroundOpacity = normalizeOpacityInput(
    opacities?.pageBackground,
    DEFAULT_OPACITIES.pageBackground,
  );
  const formBlockBackground = cssColor(colors, opacities, 'cardBackground', '#FFFFFF');
  const linkColor = cssColor(colors, opacities, 'link', '#00C8FF');

  return {
    '--hc-bg': cssColor(colors, opacities, 'pageBackground', '#FFFFFF'),
    '--hc-card': formBlockBackground,
    '--hc-container-border': cssColor(colors, opacities, 'containerBorder', '#00C8FF'),
    '--hc-text-primary': cssColor(colors, opacities, 'headingText', '#000000'),
    '--hc-text-secondary': cssColor(colors, opacities, 'bodyText', '#666666'),
    '--hc-link': linkColor,
    '--hc-chrome': cssColor(colors, opacities, 'chrome', '#00C8FF'),
    '--hc-primary': cssColor(colors, opacities, 'button', '#00C8FF'),
    '--hc-button-border': cssColor(colors, opacities, 'button', '#00C8FF'),
    '--hc-button-primary-text': cssColor(colors, opacities, 'buttonPrimaryText', '#000000'),
    '--hc-button-secondary': cssColor(
      colors,
      opacities,
      'buttonSecondary',
      normalizeHexInput(colors?.pageBackground) || '#FFFFFF',
    ),
    '--hc-button-secondary-text': cssColor(
      colors,
      opacities,
      'buttonSecondaryText',
      normalizeHexInput(colors?.button) || '#00C8FF',
    ),
    '--hc-btn1-border': cssColor(colors, opacities, 'buttonPrimaryBorder', '#00C8FF'),
    '--hc-btn1-icon': cssColor(colors, opacities, 'buttonPrimaryIcon', '#00C8FF'),
    '--hc-btn2-border': cssColor(colors, opacities, 'buttonSecondaryBorder', '#00C8FF'),
    '--hc-btn2-icon': cssColor(colors, opacities, 'buttonSecondaryIcon', '#00C8FF'),
    // Tracks the school's button colour, same as the server-side default.
    '--hc-login-button': cssColor(
      colors,
      opacities,
      'loginButton',
      normalizeHexInput(colors?.button) || '#00C8FF',
    ),
    '--hc-login-button-text': cssColor(colors, opacities, 'loginButtonText', '#FFFFFF'),
    '--hc-login-title': cssColor(colors, opacities, 'loginTitle', '#FFFFFF'),
    '--hc-login-text': cssColor(colors, opacities, 'loginText', '#B3B3B3'),
    '--hc-login-signup': linkColor,
    '--hc-login-card-bg': formBlockBackground,
    '--hc-text-subtitle': cssColor(colors, opacities, 'subheaderText', '#666666'),
    '--hc-checkbox-border': cssColor(colors, opacities, 'checkboxBorder', '#00C8FF'),
    '--hc-checkbox-check': cssColor(colors, opacities, 'checkboxCheck', '#00C8FF'),
    '--hc-email-input-title': cssColor(colors, opacities, 'emailInputTitle', '#B3B3B3'),
    '--hc-email-input-border': cssColor(colors, opacities, 'emailInputBorder', '#B3B3B3'),
    '--hc-email-input-text': cssColor(colors, opacities, 'emailInputText', '#B3B3B3'),
    '--hc-password-input-title': cssColor(colors, opacities, 'passwordInputTitle', '#B3B3B3'),
    '--hc-password-input-border': cssColor(colors, opacities, 'passwordInputBorder', '#B3B3B3'),
    '--hc-password-input-text': cssColor(colors, opacities, 'passwordInputText', '#B3B3B3'),
    '--hc-login-button-border': cssColor(
      colors,
      opacities,
      'loginButtonBorder',
      normalizeHexInput(colors?.button) || '#00C8FF',
    ),
    '--hc-email-card-blur': `${cardBlur}px`,
    '--hc-login-card-blur': `${cardBlur}px`,
    '--hc-app-primary': cssColor(colors, opacities, 'inApp', '#00C8FF'),
    '--hc-app-accent': cssColor(colors, opacities, 'inAppAccent', '#000000'),
    ...getPoweredByTextVars(pageBackgroundHex, pageBackgroundOpacity, '#000000'),
    ...getLogoSizeCssVars(headerLogoSize),
  };
}
